import React, { useState, useEffect } from 'react';
import { ModalStatusType } from '../../../types/common';
import { Form, Formik } from 'formik';
import _ from 'lodash';
import { COMPANY_MAPPING_VALIDATION } from '../../../utils/validations/validations';
import InitialValuesForm from '../../../constants/initialValues';
import usePostCompanyMapping from '../../../hooks/post/usePostCompanyMapping';
import usePutAllowedMapping from '../../../hooks/put/usePutAllowedMapping';
import useGetCompanies from '../../../hooks/get/useGetCompanies';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';

interface CompanyMapType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  loading?: boolean;
  handleRequest?: any;
  initialValue?: {
    data: any;
    id: string;
  };
  id?: string;
}

const CompanyMapModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  setModalStatus,
  loading,
  handleRequest,
  initialValue,
  id
}: CompanyMapType) => {
  const { data: companies, isLoading } = useGetCompanies(handleRequest);
  const { mutate: addCompanyMapping, isLoading: isCompanyMapAdding } = usePostCompanyMapping(
    handleRequest,
    () => {
      closeModal();
    },
    modalStatus
  );
  const { mutate: deleteCompanyMapping, isLoading: isCompanyMapDeleting } = usePutAllowedMapping(
    handleRequest,
    () => {
      closeModal();
    }
  );

  const [filteredSourceCompanies, setFilteredSourceCompanies] = useState(companies || []);
  const [filteredDestinationCompanies, setFilteredDestinationCompanies] = useState(companies || []);

  useEffect(() => {
    const sourceCompanyId = initialValue?.data?.sourceCompanyId;
    const destinationCompanyId = initialValue?.data?.destinationCompanyId;

    setFilteredSourceCompanies(
      companies?.filter((company) => company.value !== destinationCompanyId) || []
    );
    setFilteredDestinationCompanies(
      companies?.filter((company) => company.value !== sourceCompanyId) || []
    );
  }, [companies, initialValue]);

  const handleSubmit = (values: any) => {
    if (modalStatus === 'Edit') {
      deleteCompanyMapping({ payload: values, id: initialValue?.id ?? '' });
    } else {
      addCompanyMapping(values);
    }
  };

  const handleSourceCompanyChange = (value: string, setFieldValue: any) => {
    const selectedSourceCompanyId = value;
    setFieldValue('sourceCompanyId', selectedSourceCompanyId ?? null);
    setFilteredDestinationCompanies(
      companies?.filter((company) => company.value !== selectedSourceCompanyId) || []
    );
  };

  const handleDestinationCompanyChange = (value: string, setFieldValue: any) => {
    const selectedDestinationCompanyId = value;
    setFieldValue('destinationCompanyId', selectedDestinationCompanyId ?? null);
    setFilteredSourceCompanies(
      companies?.filter((company) => company.value !== selectedDestinationCompanyId) || []
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValue?.data}
      validationSchema={COMPANY_MAPPING_VALIDATION}
      onSubmit={(values: any) => handleSubmit(values)}>
      {({ touched, errors, setFieldValue, values }: any) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            valueEdited={_.isEqual(values, initialValue?.data)}
            size='sm'
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Mapping`}
            id={id}
          >
            <Form id="companyMapForm">
              <ModalBodyComponent id="companyMapModalBody">
                <div className='' id="companyMapFormContainer">
                  <div className='' id="sourceCompanyContainer">
                    <FormSelect
                      label='Source Company'
                      name='sourceCompanyId'
                      id='sourceCompanyId'
                      containerClassName='formGroup'
                      onChange={(e: any) => {
                        handleSourceCompanyChange(e, setFieldValue);
                      }}
                      errors={errors}
                      touched={touched}
                      errorValue={errors?.sourceCompanyId}
                      options={filteredSourceCompanies}
                      arrValue={values?.sourceCompanyId}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isLoading={isLoading}
                      isClearable
                      required
                    />
                  </div>
                  <div className='' id="destinationCompanyContainer">
                    <FormSelect
                      label='Destination Company'
                      name='destinationCompanyId'
                      id='destinationCompanyId'
                      containerClassName='formGroup'
                      onChange={(e: any) => {
                        handleDestinationCompanyChange(e, setFieldValue);
                      }}
                      errors={errors}
                      touched={touched}
                      errorValue={errors?.destinationCompanyId}
                      options={filteredDestinationCompanies}
                      arrValue={values?.destinationCompanyId}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isLoading={isLoading}
                      isClearable
                      required
                    />
                  </div>
                </div>
              </ModalBodyComponent>
              <ModalFooterComponent
                id="companyMapModalFooter"
                modalStatus={modalStatus}
                loading={isLoading || isCompanyMapAdding || isCompanyMapDeleting}
                onCloseModal={() => {
                  closeModal();
                }}
                valueChanged={_.isEqual(values, initialValue?.data)}
                onEditModal={() => setModalStatus('Edit')}
              />
            </Form>
          </ModalComponent>
        );
      }}
    </Formik>
  );
};

export default CompanyMapModal;
